import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import DownloadButton from "../button/DownloadButton";
import { useAxios } from "../../contexts/axiosContext";
import { parseCandidateDocUrl } from "../../utils/formatters";
import ToastService from "../../services/toastService";

const DocumentPreview = ({ selectedDoc }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const totalPages =
    Number.isFinite(selectedDoc?.totalPages) && selectedDoc.totalPages > 0
      ? selectedDoc.totalPages
      : null;

  const { authAxios } = useAxios();
  const toastService = ToastService();

  const isPdf = selectedDoc?.name?.toLowerCase().endsWith('.pdf') ??
    selectedDoc?.type?.toLowerCase().includes('pdf') ?? true;

  const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState(null);
  const [resolvedDownloadUrl, setResolvedDownloadUrl] = useState(null);

  const getPresignedUrl = async (baseDocUrl, mode) => {
    if (!baseDocUrl) return null;

    if (typeof baseDocUrl === "string" && (baseDocUrl.startsWith("blob:") || baseDocUrl.startsWith("http"))) {
      return baseDocUrl;
    }

    const parsed = parseCandidateDocUrl(baseDocUrl);
    if (!parsed) return null;

    const { candidateId, fileName } = parsed;

    try {
      const { data } = await authAxios.get(
        `/candidates/${candidateId}/documents/${fileName}/presign`,
        { params: { mode } }
      );
      return data?.url ?? null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    setPage(1);
    setResolvedPreviewUrl(null);
    setResolvedDownloadUrl(null);

    if (!selectedDoc) return;

    (async () => {
      const baseUrl = selectedDoc?.url;

      const legacyPreview = selectedDoc?.previewUrl || null;
      const legacyDownload = selectedDoc?.downloadUrl || null;

      const [finalPreview, finalDownload] = await Promise.all([
        legacyPreview ? legacyPreview : getPresignedUrl(baseUrl, "preview"),
        legacyDownload ? legacyDownload : getPresignedUrl(baseUrl, "download"),
      ]);

      if (!finalPreview && !finalDownload) {
        toastService.showErrorToast(t("EDIT.DOC_NOT_AVAILABLE"));
        return;
      }

      setResolvedPreviewUrl(finalPreview || finalDownload);
      setResolvedDownloadUrl(finalDownload || finalPreview);
    })();
  }, [selectedDoc]);

  const handleNext = () => {
    setPage((prev) => {
      if (!totalPages) return prev + 1;
      return Math.min(prev + 1, totalPages);
    });
  };

  const handlePrev = () => setPage(prev => Math.max(1, prev - 1));

  return (
    <div className="hidden lg:block w-1/2 relative">
      <div className="sticky top-4 h-[calc(100vh-32px)] flex flex-col">
        <div className="bg-white rounded-xl shadow-sm p-8 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4 gap-3">
            <h2 className="font-semibold text-darkGray text-xl shrink-0 select-none">
              {t('EDIT.PREVIEW')}
            </h2>

            {selectedDoc && (
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="text-sm bg-secondary text-darkGray px-3 py-1.5 rounded-lg font-semibold truncate max-w-48 border border-secondary shadow-sm">
                  {selectedDoc.name}
                </span>

                <DownloadButton
                  url={resolvedDownloadUrl}
                  filename={selectedDoc.name}
                  className="p-2 text-darkGray rounded-lg hover:bg-secondary border border-transparent hover:border-secondary shadow-sm bg-lightGray"
                />
              </div>
            )}
          </div>

          <div className="bg-lightGray rounded-xl border border-secondary overflow-hidden flex-1 w-full relative">
            {resolvedPreviewUrl ? (
              <iframe
                key={`${selectedDoc?.id ?? "doc"}-${page}`}
                src={isPdf
                  ? `${resolvedPreviewUrl}#page=${page}&toolbar=0&navpanes=0&view=FitH`
                  : `https://docs.google.com/viewer?url=${encodeURIComponent(resolvedPreviewUrl)}&embedded=true`
                }
                className="w-full h-full"
                title={t('EDIT.PREVIEW')}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-mediumGray p-10 text-center">
                <p className="text-sm">
                  {t('EDIT.SELECT_DOC')}
                </p>
              </div>
            )}
          </div>

          {resolvedPreviewUrl && isPdf && (
            <div className="mt-2 flex items-center justify-center relative w-full pt-3 border-t border-lightGray">
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  disabled={page === 1}
                  className="p-1 hover:bg-lightGray rounded-full disabled:opacity-10 text-darkGray"
                >
                  <ChevronLeft size={20} />
                </button>

                <span className="text-xs font-semibold text-darkGray tracking-widest uppercase whitespace-nowrap select-none">
                  {t('EDIT.PAGE', 'Pág.')} {page} {totalPages ? `/ ${totalPages}` : ''}
                </span>

                <button
                  onClick={handleNext}
                  disabled={totalPages !== null && page >= totalPages}
                  className="p-1 hover:bg-lightGray rounded-full text-darkGray disabled:opacity-10"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <a
                href={resolvedPreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex absolute right-0 text-primary font-semibold hover:underline underline-offset-4 items-center gap-1 uppercase tracking-wider text-xs"
              >
                <span className="whitespace-nowrap">{t('EDIT.OPEN_DOCUMENT')}</span>
                <ArrowUpRight size={14} strokeWidth={3} />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;